import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationSelector from './NotificationSelector';
import SmartInput from './SmartInput';
import DraftItemRow from './DraftItemRow';
import { parseSmartInput } from '../lib/smartInputParser';
import { formatDecimal } from '../utils/formatUtils';
import { hydrateShoppingSeedDefaults } from '../shared/shoppingSeedPricing';

const isDairyProduct = (itemName) => {
    if (!itemName) return false;
    const lowerName = itemName.toLowerCase();
    return (
        lowerName === 'süt'
        || lowerName.includes('çiğ süt')
        || lowerName.includes('köy sütü')
        || (lowerName.includes('süt') && !lowerName.includes('yoğurt') && !lowerName.includes('peynir') && !lowerName.includes('tatlı'))
    );
};

const BLOCKED_SHOPPING_ITEM_NAMES = [
    'içme suyu',
    'sıcak su',
    'sıcaksu',
    'kaynar su',
    'demlenmiş çay',
    'demlenmis cay'
];

const normalizeShoppingItemName = (value) => (
    String(value || '')
        .toLocaleLowerCase('tr-TR')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
);

const isBlockedShoppingItem = (value) => {
    const normalizedName = normalizeShoppingItemName(value);
    if (!normalizedName) return false;

    return BLOCKED_SHOPPING_ITEM_NAMES.some((entry) => {
        const normalizedEntry = normalizeShoppingItemName(entry);
        return normalizedName === normalizedEntry;
    });
};

const STOCK_SORT_FACTORS = {
    g: 1,
    gr: 1,
    ml: 1,
    adet: 1,
    ad: 1,
    tane: 1,
    kg: 1000,
    lt: 1000,
    l: 1000
};

const normalizeStockUnit = (value) => (
    String(value || '')
        .trim()
        .toLocaleLowerCase('tr-TR')
);

const getNormalizedStockAmount = (item = {}) => {
    const rawAmount = Number(item.amount ?? item.currentStock ?? item.suggestedAmount ?? 0);
    if (!Number.isFinite(rawAmount) || rawAmount <= 0) return 0;

    const factor = STOCK_SORT_FACTORS[normalizeStockUnit(item.canonicalUnit || item.unit)] || 1;
    return rawAmount * factor;
};

const getShoppingLinePrice = (item = {}) => Number(
    item?.price || item?.priceAtTime || item?.estimatedLineTotal || 0
) || 0;

const MarketListWizard = ({ isOpen, onClose, initialTab = 'stoklar', onSuccess }) => {
    const navigate = useNavigate();
    const [inventory, setInventory] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [cart, setCart] = useState({});
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [listName, setListName] = useState('');
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [isCartModalOpen, setIsCartModalOpen] = useState(false);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [manualType, setManualType] = useState('KITCHEN'); // 'KITCHEN' or 'GENERAL'
    const [isPasteMode, setIsPasteMode] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [ambiguityData, setAmbiguityData] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const textareaRef = React.useRef(null);
    const listNameRef = React.useRef(null);
    const [showNameError, setShowNameError] = useState(false);
    const [isDictating, setIsDictating] = useState(false);
    const [showDictatePrompt, setShowDictatePrompt] = useState(false);
    const lastMicToggleRef = useRef(0);

    // Filter & Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('stoklar'); // 'stoklar' | 'akilli' | 'bebek'
    const [stockSortMode, setStockSortMode] = useState('azalan_stok');

    // Notification State
    const [notificationSettings, setNotificationSettings] = useState({ enabled: false, date: 'tomorrow', time: '09:00' });

    // Editing Modal State
    const [editingItem, setEditingItem] = useState(null);
    const [allergenWarning, setAllergenWarning] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);

    useEffect(() => {
        if (isOpen && initialTab) {
            setActiveTab(initialTab);
        }
    }, [isOpen, initialTab]);

    const hasBabyProfile = useMemo(() => {
        return Boolean(
            userProfile?.hasBaby
            || userProfile?.profile?.hasBaby
            || userProfile?.household_children?.length
            || userProfile?.profile?.household_children?.length
        );
    }, [userProfile]);

    useEffect(() => {
        // Initialize SpeechRecognition
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recog = new SpeechRecognition();
            recog.continuous = false; // Set to false to auto-stop after a phrase
            recog.interimResults = false;
            recog.lang = 'tr-TR';
            recog.onresult = (event) => {
                const results = Array.from(event.results).map(r => r[0].transcript).join(' ');
                if (isPasteMode) {
                    setPasteText(prev => (prev ? prev + '\n' + results : results));
                } else {
                    setPasteText(prev => (prev ? prev + '\n' + results : results));
                    setIsPasteMode(true);
                }
            };
            recog.onend = () => {
                setIsListening(false);
            };
            recognitionRef.current = recog;
        }

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('modal-open');
            document.documentElement.classList.add('modal-open');
            fetchData();
        } else {
            document.body.style.overflow = 'unset';
            document.body.classList.remove('modal-open');
            document.documentElement.classList.remove('modal-open');
        }

        return () => {
            document.body.style.overflow = 'unset';
            document.body.classList.remove('modal-open');
            document.documentElement.classList.remove('modal-open');
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isManualModalOpen) {
            if (isListening) {
                try { recognitionRef.current?.stop(); } catch (e) { }
                setIsListening(false);
            }
            setIsDictating(false);
            setShowDictatePrompt(false);
        }
    }, [isManualModalOpen]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const headers = { 'Authorization': `Bearer ${token}` };

            const [invRes, sugRes, userRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/shopping/inventory-aggregated`, { headers }),
                fetch(`${API_BASE_URL}/api/shopping/suggested`, { headers }),
                fetch(`${API_BASE_URL}/api/profile`, { headers })
            ]);

            const invData = invRes.ok ? await invRes.json() : [];
            const sugData = sugRes.ok ? await sugRes.json() : [];
            const userData = userRes.ok ? await userRes.json() : null;

            setUserProfile(userData);

            // Sort suggestions: Baby items first
            const sortedSuggestions = Array.isArray(sugData)
                ? [...sugData].sort((a, b) => (b.isBabyItem ? 1 : 0) - (a.isBabyItem ? 1 : 0))
                : [];

            setInventory(Array.isArray(invData) ? invData.filter(item => !isBlockedShoppingItem(item?.name)) : []);
            setSuggestions(sortedSuggestions.filter(item => !isBlockedShoppingItem(item?.name)));
            setCart({});

        } catch (err) {
            console.error('Data fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEditModal = (e, item) => {
        if (e) e.stopPropagation();
        const existing = cart[item.name] || {};
        setEditingItem({
            ingredientId: item.ingredientId,
            name: item.name,
            amount: existing.amount || item.suggestedAmount || item.amount || 1,
            unit: existing.unit || item.unit || 'adet',
            category: item.category,
            brand: existing.brand || item.brand || '',
            alternativeProduct: existing.alternativeProduct || item.alternativeProduct || '',
            price: existing.price || getShoppingLinePrice(item),
            defaultPrice: existing.defaultPrice || item.defaultPrice || 0,
            defaultPriceUnit: existing.defaultPriceUnit || item.defaultPriceUnit || '',
            defaultPriceSource: existing.defaultPriceSource || item.defaultPriceSource || 'NONE',
            requiresBrand: Boolean(existing.requiresBrand ?? item.requiresBrand),
            originalItem: item,
            isPasteurized: existing.is_pasteurized ?? true
        });
    };

    const toggleCheckbox = (e, item) => {
        if (e) e.stopPropagation();
        setCart(prev => {
            const next = { ...prev };
            if (next[item.name]) {
                delete next[item.name];
            } else {
                next[item.name] = {
                    ingredientId: item.ingredientId,
                name: item.name,
                amount: item.suggestedAmount || item.amount || 1,
                unit: item.unit || 'adet',
                category: item.category,
                brand: '',
                alternativeProduct: '',
                itemGroup: 'KITCHEN'
            };
            }
            return next;
        });
    };

    const updateAmountDirectly = (e, itemName, sourceItem) => {
        if (e) e.stopPropagation();
        const val = parseFloat(e.target.value) || 0;
        setCart(prev => {
            if (!prev[itemName]) {
                return {
                    ...prev,
                    [itemName]: {
                        ingredientId: sourceItem.ingredientId,
                        name: itemName,
                        amount: val,
                        unit: sourceItem.unit || 'adet',
                        category: sourceItem.category,
                        brand: '',
                        alternativeProduct: '',
                        itemGroup: 'KITCHEN'
                    }
                };
            }
            return {
                ...prev,
                [itemName]: { ...prev[itemName], amount: val, itemGroup: prev[itemName].itemGroup || 'KITCHEN' }
            };
        });
    };

    const saveEditModal = async () => {
        if (!editingItem) return;

        // Also update local cart state (for new items or cart preview)
        setCart(prev => ({
            ...prev,
            [editingItem.name]: {
                ingredientId: editingItem.ingredientId,
                name: editingItem.name,
                amount: parseFloat(editingItem.amount) || 1,
                unit: editingItem.unit,
                category: editingItem.category,
                brand: editingItem.brand || '',
                alternativeProduct: editingItem.alternativeProduct || '',
                price: getShoppingLinePrice(hydrateShoppingSeedDefaults(editingItem)),
                defaultPrice: Number(editingItem.defaultPrice || 0),
                defaultPriceUnit: editingItem.defaultPriceUnit || '',
                defaultPriceSource: editingItem.defaultPriceSource || 'NONE',
                requiresBrand: Boolean(editingItem.requiresBrand),
                itemGroup: editingItem.itemGroup || 'KITCHEN',
                is_pasteurized: editingItem.isPasteurized
            }
        }));
        setEditingItem(null);
    };

    const toggleListening = (e) => {
        // Guard against double clicks
        const now = Date.now();
        if (lastMicToggleRef.current && (now - lastMicToggleRef.current) < 700) return;
        lastMicToggleRef.current = now;

        const isMobile = typeof navigator !== 'undefined' && (/Mobi|Android|iPhone|iPad/.test(navigator.userAgent) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));

        if (isMobile) {
            if (!isDictating) {
                setShowDictatePrompt(true);
                return;
            } else {
                try { textareaRef.current && textareaRef.current.blur(); } catch (err) { }
                setIsDictating(false);
                return;
            }
        }

        if (!recognitionRef.current) {
            alert('Tarayıcınız sesle yazmayı desteklemiyor');
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            try {
                recognitionRef.current.start();
                setIsListening(true);
            } catch (e) {
                console.error('Speech recognition start failed:', e);
            }
        }
    };

    const confirmDictation = () => {
        setShowDictatePrompt(false);
        setIsDictating(true);
        setTimeout(() => {
            try { textareaRef.current && textareaRef.current.focus(); } catch (e) { console.error(e); }
        }, 50);
    };

    const confirmAdd = (item) => {
        if (isBlockedShoppingItem(item?.name)) {
            return;
        }
        const itemName = (item.name || '').toLowerCase().trim();
        const BLOCKED = ['sıcak su', 'sıcaksu', 'kaynar su', 'buz', 'tuz', 'karabiber'];

        if (BLOCKED.some(b => itemName === b || itemName.includes(b))) {
            return;
        }

        setCart(prev => ({
            ...prev,
            [item.name]: {
                ingredientId: item.id?.toString().startsWith('legacy') ? null : item.id,
                name: item.name,
                amount: item.amount || 1,
                unit: item.unit || 'adet',
                category: (item.category || (manualType === 'KITCHEN' ? 'Mutfak' : 'Genel')),
                brand: item.brand || '',
                alternativeProduct: item.alternativeProduct || '',
                price: getShoppingLinePrice(hydrateShoppingSeedDefaults(item)),
                defaultPrice: Number(item.defaultPrice || 0),
                defaultPriceUnit: item.defaultPriceUnit || '',
                defaultPriceSource: item.defaultPriceSource || 'NONE',
                requiresBrand: Boolean(item.requiresBrand),
                itemGroup: manualType,
                type: item.type // PRODUCT or FAMILY
            }
        }));

        setAllergenWarning(null);
    };

    const openResolvedItemEditor = (item) => {
        if (isBlockedShoppingItem(item?.name)) {
            return;
        }
        const itemName = (item.name || '').toLowerCase().trim();
        const blocked = ['sıcak su', 'sıcaksu', 'kaynar su', 'buz', 'tuz', 'karabiber'];

        if (blocked.some((entry) => itemName === entry || itemName.includes(entry))) {
            return;
        }

        setEditingItem(hydrateShoppingSeedDefaults({
            ingredientId: item.id?.toString().startsWith('legacy') ? null : (item.id || item.dictionaryId || null),
            name: item.name,
            amount: item.amount || 1,
            unit: item.unit || 'adet',
            category: item.category || (manualType === 'KITCHEN' ? 'Mutfak' : 'Genel'),
            brand: item.brand || '',
            alternativeProduct: item.alternativeProduct || '',
            price: getShoppingLinePrice(item),
            defaultPrice: item.defaultPrice || 0,
            defaultPriceUnit: item.defaultPriceUnit || '',
            defaultPriceSource: item.defaultPriceSource || 'NONE',
            requiresBrand: Boolean(item.requiresBrand),
            itemGroup: manualType,
            originalItem: null,
            isPasteurized: item.is_pasteurized ?? true,
            type: item.type || 'PRODUCT'
        }));

        setAllergenWarning(null);
    };

    const openManualAdd = (type) => {
        setManualType(type);
        setPasteText('');
        setIsManualModalOpen(true);
    };

    const handleSaveList = async (fromModal = false) => {
        const cartArray = Object.values(cart);
        if (cartArray.length === 0) return;

        const unresolvedKitchenItems = cartArray.filter((item) => {
            const itemGroup = String(item.itemGroup || 'KITCHEN').toUpperCase();
            return itemGroup === 'KITCHEN' && !String(item.dictionaryId || item.ingredientId || '').trim();
        });
        if (unresolvedKitchenItems.length > 0) {
            alert('Mutfak ürünleri için önce listeden doğru ürünü seçmelisin.');
            return;
        }

        // Validation: If listName is empty and fromModal is true, focus and show error
        if (!listName.trim() && fromModal) {
            setIsCartModalOpen(false);
            setShowNameError(true);
            setTimeout(() => {
                listNameRef.current?.focus();
            }, 300);
            return;
        }

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('authToken');
            let finalName = listName.trim();
            if (!finalName) {
                const userName = userProfile?.name || 'Şefim';
                const today = new Date().toLocaleDateString('tr-TR');
                finalName = `${userName} - ${today}`;
            }
            const payload = {
                name: finalName,
                items: cartArray.map(i => ({
                    ingredientId: i.ingredientId,
                    dictionaryId: i.dictionaryId || i.ingredientId || null,
                    name: i.name,
                    suggestedAmount: i.amount, unit: i.unit, brand: i.brand,
                    alternativeProduct: i.alternativeProduct || '',
                    price: getShoppingLinePrice(i),
                    itemGroup: i.itemGroup || 'KITCHEN',
                    is_pasteurized: i.is_pasteurized ?? true
                })),
                // Add notification settings if enabled
                notification: notificationSettings.enabled ? {
                    date: notificationSettings.date,
                    time: notificationSettings.time
                } : null
            };
            const res = await fetch(`${API_BASE_URL}/api/shopping/lists`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                // Sync TopBar and Sidebar badges
                window.dispatchEvent(new Event('Alakart_SYNC'));
                window.dispatchEvent(new Event('shoppingListUpdated'));
                if (onSuccess) onSuccess();
                onClose();
                navigate('/my-shopping-lists');
            } else {
                const errorPayload = await res.json().catch(() => ({}));
                alert(errorPayload.message || 'Liste kaydedilemedi. Ürün eşleşmelerini kontrol etmelisin.');
            }
        } catch (err) { console.error('Save error:', err); } finally { setIsSubmitting(false); }
    };

    const sortItemsForDisplay = (items, mode) => {
        const list = [...items];
        if (mode === 'a_z') {
            return list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr'));
        }

        return list.sort((a, b) => {
            const aAmount = getNormalizedStockAmount(a);
            const bAmount = getNormalizedStockAmount(b);
            if (aAmount !== bAmount) return bAmount - aAmount;
            return String(a.name || '').localeCompare(String(b.name || ''), 'tr');
        });
    };

    // Filtering Logic
    const filteredInventory = useMemo(() => {
        let list = inventory;
        if (searchQuery) {
            list = list.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return sortItemsForDisplay(list, stockSortMode);
    }, [inventory, searchQuery, stockSortMode]);

    const filteredSuggestions = useMemo(() => {
        let list = suggestions;
        if (searchQuery) {
            list = list.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return sortItemsForDisplay(list, stockSortMode);
    }, [suggestions, searchQuery, stockSortMode]);

    const filteredBabyItems = useMemo(() => {
        const babySuggestionMap = new Map();
        suggestions
            .filter(item => item.isBabyItem)
            .forEach((item) => {
                babySuggestionMap.set(item.name, item);
            });

        inventory
            .filter(item => item.isBabyItem)
            .forEach((item) => {
                if (!babySuggestionMap.has(item.name)) {
                    babySuggestionMap.set(item.name, {
                        ...item,
                        insight: 'Bebeklerin sık tüketeceği malzemeler arasında.',
                        suggestedAmount: item.amount,
                        currentStock: item.amount
                    });
                }
            });

        let list = Array.from(babySuggestionMap.values());
        if (searchQuery) {
            list = list.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return sortItemsForDisplay(list, stockSortMode);
    }, [inventory, suggestions, searchQuery, stockSortMode]);

    const activeListItems = useMemo(() => {
        if (activeTab === 'akilli') return filteredSuggestions;
        if (activeTab === 'bebek') return filteredBabyItems;
        return filteredInventory;
    }, [activeTab, filteredInventory, filteredSuggestions, filteredBabyItems]);

    const activeTabDescription = useMemo(() => {
        if (activeTab === 'akilli') {
            return 'Alman önerilir bölümünde sistem, eksik kalan ürünleri önerilen satın alma miktarıyla gösterir.';
        }
        if (activeTab === 'bebek') {
            return 'Bebek için bölümünde, bebeklerin sık tükettiği ve yakından takip edilmesi gereken ürünler yer alır.';
        }
        return 'Stokta var bölümünde mutfağınızdaki gerçek mevcut miktarlar görünür.';
    }, [activeTab]);

    const cartCount = Object.keys(cart).length;

    return (<><AnimatePresence>
        {isOpen && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm overflow-y-auto overflow-x-hidden font-sans no-scrollbar flex items-center justify-center p-4 lg:p-0"
            >
                {loading ? (
                    <div className="h-screen w-full flex items-center justify-center bg-black/20 backdrop-blur-md transition-colors">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                ) : (
                    <div className="relative w-full flex items-center justify-center min-h-screen">
                        {/* MOBILE VIEW (lg altı) */}
                        <div className="lg:hidden w-full h-screen bg-white dark:bg-slate-900 shadow-2xl overflow-y-auto no-scrollbar flex flex-col">
                            <header className="pt-8 pb-4 px-6 sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800 transition-colors duration-300">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1 pr-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="material-symbols-outlined text-primary text-3xl">shopping_cart_checkout</span>
                                            <h1 className="text-xl font-bold leading-tight">Alışveriş Listesi Hazırlayın</h1>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 leading-snug">Sık kullandığın ürünleri seçerek hızlıca envanterini oluştur.</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={onClose} className="shrink-0 w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400">
                                            <span className="material-symbols-outlined">close</span>
                                        </button>
                                    </div>
                                </div>
                            </header>

                            {/* Truly Floating Cart FAB (lg:hidden) */}
                            <div className="lg:hidden fixed top-20 right-5 z-[100] pointer-events-none">
                                <button
                                    onClick={() => setIsCartModalOpen(true)}
                                    className="pointer-events-auto relative flex items-center justify-center w-12 h-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg text-primary transition-transform active:scale-95 hover:scale-105"
                                >
                                    <span className="material-symbols-outlined text-2xl">shopping_cart</span>
                                    {cartCount > 0 && (
                                        <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-black text-white shadow-md border-2 border-white dark:border-gray-800">
                                            {cartCount}
                                        </span>
                                    )}
                                </button>
                            </div>


                            <main className="px-4 pb-32 w-full flex-1">
                                {/* Tabs & Search */}
                                <div className="mb-5 mt-4">
                                    <h2 className="text-sm font-semibold tracking-wide text-gray-900 dark:text-gray-200 mb-3 px-2">Ürünlerinizi Seçin</h2>
                                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 px-1 mb-2">
                                        <button
                                            onClick={() => setActiveTab('stoklar')}
                                            className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full shadow-sm transition-colors flex items-center gap-1 ${activeTab === 'stoklar' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
                                        >
                                            <span className="material-symbols-outlined text-sm">inventory</span>
                                            Stokta Var
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('akilli')}
                                            className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full shadow-sm transition-colors flex items-center gap-1 ${activeTab === 'akilli' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
                                        >
                                            <span className="material-symbols-outlined text-sm">settings</span>
                                            Alman Önerilir
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('bebek')}
                                            className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full shadow-sm transition-colors flex items-center gap-1 ${activeTab === 'bebek' ? 'bg-blue-500 text-white border-blue-600' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'} ${hasBabyProfile ? '' : 'hidden'}`}
                                        >
                                            <span className="material-symbols-outlined text-sm">child_care</span>
                                            Bebek İçin
                                        </button>
                                    </div>
                                    <div className="px-1 mb-2 flex items-start justify-between gap-3">
                                        <p className="text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                                            {activeTabDescription}
                                        </p>
                                        <select
                                            value={stockSortMode}
                                            onChange={(e) => setStockSortMode(e.target.value)}
                                            className="shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300"
                                        >
                                            <option value="azalan_stok">Azalan Stok</option>
                                            <option value="a_z">A-Z</option>
                                        </select>
                                    </div>
                                    <div className="relative px-1">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                            <span className="material-symbols-outlined text-gray-400">search</span>
                                        </div>
                                        <input
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="block w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border-none rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 shadow-sm focus:ring-2 focus:ring-primary/50 sm:text-sm transition-shadow outline-none"
                                            placeholder="Ürün, kategori veya marka ara..."
                                            type="text"
                                        />
                                    </div>
                                </div>

                                {/* Product Lists */}
                                <div className="space-y-3">
                                    {activeListItems.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm transition-all duration-200">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-gray-900 flex items-center justify-center shadow-sm text-primary">
                                                    <span className="material-symbols-outlined text-2xl">
                                                        {item.isBabyItem ? 'child_care' : 'nutrition'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 dark:text-white leading-tight">{item.name}</h3>
                                                    {(cart[item.name]?.brand || cart[item.name]?.alternativeProduct) && (
                                                        <p className="text-[10px] text-primary font-bold">
                                                            {cart[item.name]?.brand || 'Marka yok'}
                                                            {cart[item.name]?.alternativeProduct ? ` • Alt: ${cart[item.name].alternativeProduct}` : ''}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center bg-gray-50 dark:bg-gray-900 rounded-lg p-1 gap-2 border border-gray-100 dark:border-gray-800">
                                                <input
                                                    type="number"
                                                    className="w-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm text-center outline-none focus:ring-1 focus:ring-primary"
                                                    value={cart[item.name]?.amount || ''}
                                                    placeholder={item.suggestedAmount || item.amount || '1'}
                                                    onChange={(e) => updateAmountDirectly(e, item.name, item)}
                                                />
                                                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase">{cart[item.name]?.unit || item.unit || 'adet'}</span>
                                                <button
                                                    onClick={(e) => handleOpenEditModal(e, item)}
                                                    className="w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-500 hover:text-primary transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-sm">edit</span>
                                                </button>
                                                <label className="relative flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!cart[item.name]}
                                                        onChange={(e) => toggleCheckbox(e, item)}
                                                        className="peer appearance-none w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded checked:bg-primary checked:border-primary transition-colors cursor-pointer"
                                                    />
                                                    <span className="material-symbols-outlined absolute text-white opacity-0 peer-checked:opacity-100 text-sm pointer-events-none">check</span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>



                                {/* Manual Buttons (Integrated into Mobile Flow - will be in Cart Modal instead of Floating) */}
                            </main>
                        </div >

                        {/* DESKTOP VIEW (lg+) */}
                        < div className="hidden lg:flex items-center justify-center min-h-screen p-6" >
                            <div className="bg-white dark:bg-slate-800 w-full max-w-[1200px] h-[800px] rounded-3xl shadow-2xl border border-white/50 dark:border-gray-800 flex flex-col overflow-hidden ring-1 ring-black/5 relative z-10 transition-colors">
                                <div className="px-8 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm z-10 shrink-0">
                                    <div>
                                        <h2 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-3">
                                            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-xl text-primary">
                                                <span className="material-symbols-outlined text-[28px]">inventory_2</span>
                                            </div>
                                            Akıllı Alışveriş Hazırlığı
                                        </h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 font-medium pl-14">
                                            Eksikleri tespit edin, miktarları belirleyin ve listenizi tek dokunuşla oluşturun.
                                        </p>
                                    </div>
                                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <span className="material-symbols-outlined">close</span>
                                    </button>
                                </div>

                                <div className="flex-1 flex overflow-hidden">
                                    {/* COL 1: STOKLARIM */}
                                    <div className="w-1/3 flex flex-col border-r border-gray-100 dark:border-gray-800 bg-white dark:bg-slate-800">
                                        <div className="p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-900/30">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-black flex items-center gap-2 dark:text-gray-200">
                                                    <span className="material-symbols-outlined text-[20px] text-primary">inventory</span>
                                                    Stokta Var
                                                </h3>
                                                <div className="flex items-center gap-2">
                                                    <select
                                                        value={stockSortMode}
                                                        onChange={(e) => setStockSortMode(e.target.value)}
                                                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-[11px] py-1.5 px-2 shadow-sm dark:text-gray-200"
                                                    >
                                                        <option value="azalan_stok">Azalan Stok</option>
                                                        <option value="a_z">A-Z</option>
                                                    </select>
                                                    <div className="relative">
                                                        <input
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            className="bg-white dark:bg-gray-800 border-none rounded-lg text-xs py-1.5 pl-8 pr-3 shadow-sm focus:ring-1 focus:ring-primary w-40 dark:text-gray-200"
                                                            placeholder="Ara..."
                                                        />
                                                        <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-[14px] text-gray-400">search</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                                                Stokta var bölümünde mutfağınızdaki gerçek mevcut miktarlar görünür.
                                            </p>
                                        </div>
                                        <div className="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar">
                                            {filteredInventory.map((item, idx) => (
                                                <div key={idx} className="group flex items-center gap-3 p-3 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-700 rounded-2xl hover:border-primary/30 hover:shadow-md transition-all">
                                                    <div className="size-11 shrink-0 rounded-xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-2xl">🛒</div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-xs font-black text-gray-900 dark:text-white truncate">{item.name}</h4>
                                                        {(cart[item.name]?.brand || cart[item.name]?.alternativeProduct) && (
                                                            <p className="text-[10px] font-bold text-primary">
                                                                {cart[item.name]?.brand || 'Marka yok'}
                                                                {cart[item.name]?.alternativeProduct ? ` • Alt: ${cart[item.name].alternativeProduct}` : ''}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 p-1.5 rounded-xl border border-gray-100 dark:border-gray-700 shrink-0">
                                                        <input
                                                            className="w-10 h-8 text-xs text-center bg-white dark:bg-gray-800 border-none rounded-lg focus:ring-1 focus:ring-primary p-0 font-black dark:text-gray-200"
                                                            type="number"
                                                            value={cart[item.name]?.amount || ''}
                                                            placeholder={item.amount || '1'}
                                                            onChange={(e) => updateAmountDirectly(e, item.name, item)}
                                                        />
                                                        <span className="text-[10px] font-black text-gray-500 px-1 uppercase">{cart[item.name]?.unit || item.unit || 'adet'}</span>
                                                        <button onClick={(e) => handleOpenEditModal(e, item)} className="size-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-400 hover:text-primary transition-all">
                                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                                        </button>
                                                        <label className="relative flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg cursor-pointer">
                                                            <input className="peer appearance-none w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded-md checked:bg-primary checked:border-primary transition-colors cursor-pointer" type="checkbox" checked={!!cart[item.name]} onChange={(e) => toggleCheckbox(e, item)} />
                                                            <span className="material-symbols-outlined absolute text-white opacity-0 peer-checked:opacity-100 text-sm pointer-events-none">check</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* COL 2: AKILLI SEÇİM */}
                                    <div className="w-1/3 flex flex-col border-r border-gray-100 dark:border-gray-800 bg-gray-50/20 dark:bg-gray-900/20">
                                        <div className="p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-900/30">
                                            <h3 className="text-sm font-black flex items-center gap-2 dark:text-gray-200">
                                                <span className="material-symbols-outlined text-[20px] text-primary">recommend</span>
                                                Alman Önerilir
                                            </h3>
                                            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                                                Alman önerilir bölümünde sistem, önerilen satın alma miktarını gösterir.
                                            </p>
                                        </div>
                                        <div className="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar">
                                            {filteredSuggestions.map((item, idx) => (
                                                <div key={idx} className="group flex items-center gap-3 p-3 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-700 rounded-2xl hover:border-primary/30 hover:shadow-md transition-all">
                                                    <div className="size-11 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-2xl relative shadow-sm">
                                                        {item.isBabyItem ? '👶' : '🛒'}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-xs font-black text-gray-900 dark:text-white truncate">{item.name}</h4>
                                                        <p className="text-[10px] leading-tight text-gray-400 dark:text-gray-500 line-clamp-1 italic font-medium">
                                                            {cart[item.name]?.brand || cart[item.name]?.alternativeProduct ? (
                                                                <span className="text-primary not-italic font-black">
                                                                    Marka: {cart[item.name]?.brand || 'yok'}
                                                                    {cart[item.name]?.alternativeProduct ? ` • Alt: ${cart[item.name].alternativeProduct}` : ''}
                                                                </span>
                                                            ) : (item.insight || item.whyToBuy)}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 p-1.5 rounded-xl border border-gray-100 dark:border-gray-700 shrink-0">
                                                        <input className="w-10 h-8 text-xs text-center bg-white dark:bg-gray-800 border-none rounded-lg focus:ring-1 focus:ring-primary p-0 font-black dark:text-gray-200" type="number" value={cart[item.name]?.amount || ''} placeholder={item.suggestedAmount || '1'} onChange={(e) => updateAmountDirectly(e, item.name, item)} />
                                                        <span className="text-[10px] font-black text-gray-500 px-1 uppercase">{cart[item.name]?.unit || item.unit || 'adet'}</span>
                                                        <button onClick={(e) => handleOpenEditModal(e, item)} className="size-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-400 hover:text-primary transition-all">
                                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                                        </button>
                                                        <label className="relative flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg cursor-pointer">
                                                            <input className="peer appearance-none w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded-md checked:bg-primary checked:border-primary transition-colors cursor-pointer" type="checkbox" checked={!!cart[item.name]} onChange={(e) => toggleCheckbox(e, item)} />
                                                            <span className="material-symbols-outlined absolute text-white opacity-0 peer-checked:opacity-100 text-sm pointer-events-none">check</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* COL 3: SEPETİM */}
                                    <div className="w-1/3 flex flex-col bg-gray-50/50 dark:bg-gray-900/50 border-l border-gray-100 dark:border-gray-800">
                                        <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-slate-800 relative z-20">
                                            <h3 className="text-sm font-black flex items-center gap-2 dark:text-gray-200">
                                                <span className="material-symbols-outlined text-[20px] text-primary">shopping_basket</span>
                                                Hazırlanan Liste
                                            </h3>
                                            <div className="bg-primary text-white text-[11px] font-black px-3 py-1 rounded-full shadow-lg shadow-primary/20">{cartCount} Ürün</div>
                                        </div>

                                        {/* Fixed Desktop Entry Bar */}
                                        <div className="px-5 py-3 bg-gray-50/80 dark:bg-gray-900/40 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800 flex gap-2 relative z-10 shrink-0">
                                            <button onClick={() => openManualAdd('KITCHEN')} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-primary/20 bg-white dark:bg-gray-800 text-primary hover:bg-primary hover:text-white transition-all font-black text-[9px] uppercase tracking-wider group shadow-sm">
                                                <span className="material-symbols-outlined text-[14px] group-hover:scale-110 transition-transform">restaurant</span>
                                                Mutfak Ekle
                                            </button>
                                            <button onClick={() => openManualAdd('GENERAL')} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:border-slate-400 hover:text-slate-600 transition-all font-black text-[9px] uppercase tracking-wider group shadow-sm">
                                                <span className="material-symbols-outlined text-[14px] group-hover:scale-110 transition-transform">list_alt</span>
                                                Genel Ekle
                                            </button>
                                        </div>

                                        <div className="flex-1 overflow-y-auto p-5 pt-2 space-y-4 no-scrollbar">
                                            {/* Kitchen Section */}
                                            <div>
                                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-[14px]">restaurant</span>
                                                    Mutfak Malzemeleri
                                                </h4>
                                                <div className="space-y-3">
                                                    <AnimatePresence>
                                                        {Object.values(cart).filter(i => i.itemGroup !== 'GENERAL').map((item) => (
                                                            <motion.div key={item.name} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95, x: 50 }} onClick={(e) => handleOpenEditModal(e, item)} className="flex items-center justify-between p-4 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm cursor-pointer hover:border-primary/20 group transition-all">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="size-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-xl">🛒</div>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[13px] font-black text-gray-900 dark:text-white leading-none mb-1.5">{item.name}</span>
                                                                        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-500">
                                                                            <span className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded uppercase">{formatDecimal(item.amount)} {item.unit}</span>
                                                                            {item.brand && <span className="text-primary">| {item.brand}</span>}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <button onClick={(e) => { e.stopPropagation(); toggleCheckbox(e, item); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all p-2"><span className="material-symbols-outlined">delete</span></button>
                                                            </motion.div>
                                                        ))}
                                                    </AnimatePresence>
                                                </div>
                                            </div>

                                            {/* General Section */}
                                            <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-[14px]">list_alt</span>
                                                    Diğer / Genel
                                                </h4>
                                                <div className="space-y-3">
                                                    <AnimatePresence>
                                                        {Object.values(cart).filter(i => i.itemGroup === 'GENERAL').map((item) => (
                                                            <motion.div key={item.name} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95, x: 50 }} onClick={(e) => handleOpenEditModal(e, item)} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-sm cursor-pointer hover:border-primary/20 group transition-all">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="size-10 rounded-xl bg-gray-100 dark:bg-gray-900 flex items-center justify-center text-xl">📌</div>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[13px] font-black text-gray-500 dark:text-gray-400 leading-none mb-1.5">{item.name}</span>
                                                                        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400">
                                                                            <span className="bg-white dark:bg-gray-700 px-1.5 py-0.5 rounded uppercase">{formatDecimal(item.amount)} {item.unit}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <button onClick={(e) => { e.stopPropagation(); toggleCheckbox(e, item); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all p-2"><span className="material-symbols-outlined">delete</span></button>
                                                            </motion.div>
                                                        ))}
                                                    </AnimatePresence>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-6 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-gray-700 space-y-4">
                                            {/* Notification Selector (Desktop) */}
                                            <NotificationSelector
                                                onChange={setNotificationSettings}
                                                initialData={notificationSettings}
                                            />
                                            <div>
                                                <label className="text-[10px] font-black text-gray-400 uppercase mb-2 block tracking-widest pl-1">LİSTE ADI (İsteğe Bağlı)</label>
                                                <input
                                                    ref={listNameRef}
                                                    className={`w-full px-5 py-4 text-sm font-bold border rounded-2xl ${showNameError ? 'border-red-500 ring-2 ring-red-500/50 animate-pulse' : 'border-none'} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 outline-none transition-all`}
                                                    placeholder={showNameError ? "Lütfen listeye bir isim verip kaydet tuşuna basın" : "Haftalık Mutfak Alışverişi..."}
                                                    value={listName}
                                                    onChange={(e) => {
                                                        setListName(e.target.value);
                                                        if (showNameError) setShowNameError(false);
                                                    }}
                                                />
                                            </div>
                                            <div className="flex gap-3">
                                                <button onClick={onClose} className="flex-1 py-4 text-xs font-black text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-2xl transition-all">İptal</button>
                                                <button onClick={() => handleSaveList(true)} disabled={isSubmitting || cartCount === 0} className="flex-[2] bg-primary hover:bg-red-600 text-white font-black py-4 rounded-2xl shadow-xl shadow-primary/30 active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-3 uppercase tracking-wider text-xs">
                                                    {isSubmitting ? 'Kaydediliyor...' : 'Listeyi Tamamla'}
                                                    <span className="material-symbols-outlined text-[18px]">send</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div >

                        {/* CART MODAL (For Mobile) */}
                        < AnimatePresence >
                            {isCartModalOpen && (
                                <div className="fixed inset-0 z-[220] flex items-end justify-center bg-black/60 backdrop-blur-sm p-4 lg:hidden">
                                    <motion.div
                                        initial={{ y: "100%" }}
                                        animate={{ y: 0 }}
                                        exit={{ y: "100%" }}
                                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                        className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-t-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                                    >
                                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                                            <div>
                                                <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-primary">shopping_basket</span>
                                                    Seçilen Ürünler
                                                </h3>
                                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">{cartCount} Ürün Listelendi</p>
                                            </div>
                                            <button onClick={() => setIsCartModalOpen(false)} className="size-10 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm text-gray-400">
                                                <span className="material-symbols-outlined">close</span>
                                            </button>
                                        </div>

                                        <div className="flex-1 overflow-y-auto p-4 space-y-6 no-scrollbar">
                                            {/* Kitchen Section Mobile */}
                                            <div>
                                                <h4 className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Mutfak Malzemeleri</h4>
                                                <div className="space-y-2">
                                                    {Object.values(cart).filter(i => i.itemGroup !== 'GENERAL').length === 0 ? (
                                                        <div className="p-4 text-center border border-dashed border-gray-100 dark:border-gray-800 rounded-xl text-[10px] text-gray-400">Henüz mutfak ürünü yok</div>
                                                    ) : (
                                                        Object.values(cart).filter(i => i.itemGroup !== 'GENERAL').map((item) => (
                                                            <div key={item.name} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-gray-700 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-900/50 transition-colors px-1">
                                                                <div className="flex-1">
                                                                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 tracking-tight">
                                                                        {item.brand ? `${item.brand} ` : ''}{item.name} {formatDecimal(item.amount)} {item.unit}
                                                                        {item.alternativeProduct ? ` • Alt: ${item.alternativeProduct}` : ''}
                                                                    </span>
                                                                </div>
                                                                <button onClick={(e) => toggleCheckbox(e, item)} className="text-gray-400 hover:text-red-500 transition-colors ml-4"><span className="material-symbols-outlined text-base">delete</span></button>
                                                            </div>
                                                        ))
                                                    )}
                                                    <button onClick={() => openManualAdd('KITCHEN')} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 font-bold text-[10px] uppercase tracking-wider"><span className="material-symbols-outlined text-sm">add_circle</span> Mutfak Ürünü Ekle</button>
                                                </div>
                                            </div>

                                            {/* General Section Mobile */}
                                            <div className="pt-2 border-t border-gray-50 dark:border-gray-700">
                                                <h4 className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Diğer / Genel</h4>
                                                <div className="space-y-2">
                                                    {Object.values(cart).filter(i => i.itemGroup === 'GENERAL').length === 0 ? (
                                                        <div className="p-4 text-center border border-dashed border-gray-100 dark:border-gray-800 rounded-xl text-[10px] text-gray-400">Henüz genel ürün yok</div>
                                                    ) : (
                                                        Object.values(cart).filter(i => i.itemGroup === 'GENERAL').map((item) => (
                                                            <div key={item.name} className="flex items-center justify-between py-2 border-b border-gray-50 dark:border-gray-700 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-900/50 transition-colors px-1">
                                                                <div className="flex-1">
                                                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-tight">
                                                                        {item.name} {formatDecimal(item.amount)} {item.unit}
                                                                    </span>
                                                                </div>
                                                                <button onClick={(e) => toggleCheckbox(e, item)} className="text-gray-400 hover:text-red-500 transition-colors ml-4"><span className="material-symbols-outlined text-base">delete</span></button>
                                                            </div>
                                                        ))
                                                    )}
                                                    <button onClick={() => openManualAdd('GENERAL')} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 font-bold text-[10px] uppercase tracking-wider"><span className="material-symbols-outlined text-sm">add_circle</span> Genel Ürün Ekle</button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-6 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 mb-safe-bottom">
                                            <button
                                                onClick={() => setIsNameModalOpen(true)}
                                                disabled={cartCount === 0 || isSubmitting}
                                                className="w-full bg-primary hover:bg-red-600 text-white font-black py-4 rounded-2xl shadow-xl shadow-primary/30 flex items-center justify-center gap-3 uppercase tracking-wider text-xs transition-all active:scale-[0.98]"
                                            >
                                                Listeyi Tamamla ve Oluştur
                                                <span className="material-symbols-outlined">arrow_forward</span>
                                            </button>
                                        </div>
                                    </motion.div>
                                </div>
                            )}
                        </AnimatePresence >

                        {/* EDIT MODAL (Global) */}
                        < AnimatePresence >
                            {editingItem && (
                                <div className="fixed inset-0 z-[320] flex items-end justify-center bg-black/40 backdrop-blur-md p-0 sm:items-center sm:p-6">
                                    <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="relative w-full max-w-none overflow-hidden rounded-t-[2rem] border border-white/50 bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl dark:border-gray-700 dark:bg-gray-800 sm:max-w-sm sm:rounded-[2.5rem] sm:p-8">
                                        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                                        <div className="text-center relative">
                                            <div className="inline-flex p-5 bg-red-50 dark:bg-red-900/20 rounded-[2rem] text-primary mb-5 shadow-inner"><span className="material-symbols-outlined text-[48px]">edit_note</span></div>
                                            <h3 className="text-2xl font-black text-gray-900 dark:text-white leading-tight">{editingItem.name}</h3>
                                            <p className="text-[11px] font-bold text-gray-400 mt-1 uppercase tracking-widest">Özellikleri Düzenleyin</p>
                                        </div>
                                        <div className="mt-8 space-y-6">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Miktar</label>
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        className="w-full h-14 text-xl font-black border-none rounded-2xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white px-5 focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                                        value={editingItem.amount}
                                                        onChange={(e) => setEditingItem({ ...editingItem, amount: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Birim</label>
                                                    <select className="w-full h-14 text-sm font-black border-none rounded-2xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white px-5 focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none cursor-pointer" value={editingItem.unit} onChange={(e) => setEditingItem({ ...editingItem, unit: e.target.value })}>
                                                        <option value="adet">adet</option><option value="kg">kg</option><option value="gr">gr</option><option value="lt">lt</option><option value="ml">ml</option><option value="paket">paket</option><option value="file">file</option><option value="koli">koli</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">
                                                    Marka
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Örn: Sütaş, İçim, Pınar..."
                                                    className="w-full h-14 text-sm font-black border-none rounded-2xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white px-5 focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                                    value={editingItem.brand || ''}
                                                    onChange={(e) => setEditingItem({ ...editingItem, brand: e.target.value })}
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">
                                                    Alternatif Marka
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Örn: Pınar yoksa İçim"
                                                    className="w-full h-14 text-sm font-black border-none rounded-2xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white px-5 focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                                    value={editingItem.alternativeProduct || ''}
                                                    onChange={(e) => setEditingItem({ ...editingItem, alternativeProduct: e.target.value })}
                                                />
                                            </div>

                                            {/* Pasteurization Check (For Dairy) */}
                                            {isDairyProduct(editingItem.name) && (
                                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800 animate-fade-in-up">
                                                    <label className="flex items-center gap-3 cursor-pointer group">
                                                        <div className="relative flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={editingItem.isPasteurized ?? true}
                                                                onChange={(e) => setEditingItem({ ...editingItem, isPasteurized: e.target.checked })}
                                                                className="peer h-6 w-6 cursor-pointer appearance-none rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-800 checked:bg-blue-600 checked:border-blue-600 transition-all"
                                                            />
                                                            <span className="material-symbols-outlined absolute opacity-0 peer-checked:opacity-100 text-white pointer-events-none text-xl left-0 top-0 h-6 w-6 flex items-center justify-center">
                                                                check
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] font-black text-blue-900 dark:text-blue-300 uppercase tracking-widest">Pastörize Ürün</span>
                                                            <span className="text-[9px] font-bold text-blue-700 dark:text-blue-400 leading-tight">Market ürünleri pastörizedir. Bebeğiniz için daha güvenlidir.</span>
                                                        </div>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-10 flex gap-4">
                                            <button onClick={() => setEditingItem(null)} className="flex-1 h-14 rounded-2xl text-xs font-black text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all uppercase tracking-widest">İptal</button>
                                            <button onClick={saveEditModal} className="flex-[2] h-14 bg-primary text-white rounded-2xl font-black text-xs shadow-xl shadow-primary/30 flex items-center justify-center gap-2 uppercase tracking-widest hover:bg-red-600 active:scale-[0.98] transition-all">Kaydet ve Ekle<span className="material-symbols-outlined text-[18px]">check_circle</span></button>
                                        </div>
                                    </motion.div>
                                </div>
                            )}
                        </ AnimatePresence >

                        {/* MANUAL ENTRY MODAL (Global) */}
                        < AnimatePresence >
                            {isManualModalOpen && (
                                <div className="fixed inset-0 z-[260] flex items-end justify-center overflow-y-auto bg-black/40 backdrop-blur-md p-0 sm:items-center sm:p-6">
                                    <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative w-full max-w-none rounded-t-[2rem] border border-white/50 bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl dark:border-gray-700 dark:bg-gray-800 sm:max-w-lg sm:rounded-[2.5rem] sm:p-8">
                                        <button onClick={() => setIsManualModalOpen(false)} className="absolute top-8 right-8 w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-slate-700/50 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
                                            <span className="material-symbols-outlined text-[24px]">close</span>
                                        </button>
                                        <h3 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-3">
                                            <span className={`material-symbols-outlined text-3xl ${manualType === 'KITCHEN' ? 'text-primary' : 'text-slate-400'}`}>
                                                {manualType === 'KITCHEN' ? 'restaurant' : 'list_alt'}
                                            </span>
                                            {manualType === 'KITCHEN' ? 'Mutfak Ürünü Ekle' : 'Genel Ürün Ekle'}
                                        </h3>
                                        <div className="mt-8">
                                            {isPasteMode ? (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between px-1">
                                                        <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Toplu Yapıştır</span>
                                                        <span className="text-[9px] font-bold text-gray-400">Her satıra bir ürün</span>
                                                    </div>
                                                    <textarea
                                                        value={pasteText}
                                                        onChange={(e) => setPasteText(e.target.value)}
                                                        placeholder="Örn:&#10;2 kg domates&#10;1 lt süt&#10;Beyaz peynir..."
                                                        className="w-full h-48 p-5 bg-gray-50 dark:bg-gray-900 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-3xl text-sm font-bold resize-none outline-none focus:border-primary transition-all"
                                                        autoFocus
                                                    />
                                                    <div className="flex gap-3">
                                                        <button 
                                                            onClick={() => { setIsPasteMode(false); setPasteText(''); }}
                                                            className="flex-1 h-12 rounded-2xl border-2 border-gray-100 dark:border-gray-800 text-[10px] font-black uppercase text-gray-400"
                                                        >
                                                            Vazgeç
                                                        </button>
                                                        <button 
                                                            onClick={async () => {
                                                                const lines = pasteText.split('\n').filter(l => l.trim().length >= 2);
                                                                if (lines.length === 0) return setIsPasteMode(false);
                                                                setIsSubmitting(true);
                                                                try {
                                                                    const res = await fetch(`${API_BASE_URL}/api/shopping/unification/resolve-batch`, {
                                                                        method: 'POST',
                                                                        headers: { 
                                                                            'Content-Type': 'application/json',
                                                                            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                                                        },
                                                                        body: JSON.stringify({ names: lines })
                                                                    });
                                                                    const results = await res.json();
                                                                    const unresolved = [];
                                                                    results.forEach((r, idx) => {
                                                                        const parsed = parseSmartInput(lines[idx]);
                                                                        if (manualType === 'KITCHEN' && (r.status !== 'RESOLVED' || !r.dictionaryId)) {
                                                                            unresolved.push({
                                                                                rawName: parsed.name || r.rawName,
                                                                                amount: parsed.amount || 1,
                                                                                unit: parsed.unit || r.canonicalUnit || 'adet',
                                                                                status: r.status || 'UNKNOWN',
                                                                                candidates: r.candidates || []
                                                                            });
                                                                            return;
                                                                        }
                                                                        confirmAdd({
                                                                            id: r.status === 'RESOLVED' ? r.dictionaryId : null,
                                                                            name: r.canonicalName || r.rawName,
                                                                            amount: parsed.amount || 1,
                                                                            unit: parsed.unit || r.canonicalUnit || 'adet',
                                                                            category: r.category || (manualType === 'KITCHEN' ? 'Mutfak' : 'Genel'),
                                                                            brand: '',
                                                                            price: 0,
                                                                            source: 'MANUAL'
                                                                        });
                                                                    });
                                                                    if (unresolved.length > 0) {
                                                                        const first = unresolved[0];
                                                                        setAmbiguityData({
                                                                            candidates: first.candidates,
                                                                            rawName: first.rawName,
                                                                            amount: first.amount,
                                                                            unit: first.unit,
                                                                            source: 'MANUAL',
                                                                            status: first.status
                                                                        });
                                                                        alert('Bazı ürünler netleşmedi. Listeye eklemeden önce doğru ürünü seçmelisin.');
                                                                    }
                                                                    setIsPasteMode(false);
                                                                    setPasteText('');
                                                                } finally { setIsSubmitting(false); }
                                                            }}
                                                            disabled={isSubmitting || !pasteText.trim()}
                                                            className="flex-[2] h-12 bg-primary text-white rounded-2xl text-[10px] font-black uppercase shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
                                                        >
                                                            {isSubmitting ? 'İşleniyor...' : 'Hepsini Çöz ve Ekle'}
                                                            <span className="material-symbols-outlined text-sm">auto_awesome</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-6">
                                                    <div className="flex items-center justify-between px-1">
                                                        <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Akıllı Giriş</span>
                                                        <button 
                                                            onClick={() => setIsPasteMode(true)}
                                                            className="text-[10px] text-primary font-black uppercase hover:underline"
                                                        >
                                                            Toplu Yapıştır
                                                        </button>
                                                    </div>
                                                    <SmartInput 
                                                        onAdd={async (item) => {
                                                            if (item.resolveStatus === 'RESOLVED') {
                                                                openResolvedItemEditor({
                                                                    id: item.dictionaryId,
                                                                    name: item.name,
                                                                    amount: item.amount || 1,
                                                                    unit: item.unit || 'adet',
                                                                    brand: item.brand || '',
                                                                    price: getShoppingLinePrice(item),
                                                                    defaultPrice: item.defaultPrice || 0,
                                                                    defaultPriceUnit: item.defaultPriceUnit || '',
                                                                    defaultPriceSource: item.defaultPriceSource || 'NONE',
                                                                    requiresBrand: Boolean(item.requiresBrand),
                                                                    type: 'PRODUCT'
                                                                });
                                                            } else {
                                                                setIsSubmitting(true);
                                                                try {
                                                                    const res = await fetch(`${API_BASE_URL}/api/shopping/unification/resolve-batch`, {
                                                                        method: 'POST',
                                                                        headers: { 
                                                                            'Content-Type': 'application/json',
                                                                            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                                                        },
                                                                        body: JSON.stringify({ names: [item.name] })
                                                                    });
                                                                    const [result] = await res.json();
                                                                    if (result) {
                                                                        if (result.status === 'RESOLVED') {
                                                                            openResolvedItemEditor({
                                                                                id: result.dictionaryId,
                                                                                name: result.canonicalName || item.name,
                                                                                amount: item.amount || 1,
                                                                                unit: result.canonicalUnit || 'adet',
                                                                                brand: item.brand || '',
                                                                                price: getShoppingLinePrice(item),
                                                                                type: 'PRODUCT'
                                                                            });
                                                                        } else {
                                                                            setAmbiguityData({
                                                                                candidates: result.candidates || [],
                                                                                rawName: item.name,
                                                                                amount: item.amount,
                                                                                unit: item.unit,
                                                                                source: "MANUAL",
                                                                                status: result.status
                                                                            });
                                                                        }
                                                                    }
                                                                } catch (err) { console.error('Wizard resolve error:', err); }
                                                                finally { setIsSubmitting(false); }
                                                            }
                                                        }}
                                                        onSelectSuggestion={(suggestion, context = {}) => {
                                                            const parsed = context?.parsed || {};
                                                            openResolvedItemEditor({
                                                                id: suggestion.id,
                                                                name: suggestion.name,
                                                                amount: parsed.amount || 1,
                                                                unit: parsed.unit || suggestion.unit || 'adet',
                                                                brand: '',
                                                                price: getShoppingLinePrice(suggestion) || suggestion.defaultPrice || 0,
                                                                defaultPrice: suggestion.defaultPrice || 0,
                                                                defaultPriceUnit: suggestion.defaultPriceUnit || '',
                                                                defaultPriceSource: suggestion.defaultPriceSource || 'NONE',
                                                                requiresBrand: Boolean(suggestion.requiresBrand),
                                                                type: suggestion.type || 'PRODUCT'
                                                            });
                                                        }}
                                                        mode="wizard" 
                                                    />
                                                    <div className="flex justify-end pr-2">
                                                        <button
                                                            onClick={toggleListening}
                                                            className={`p-2 rounded-xl transition-all ${(isListening || isDictating) ? 'text-primary animate-pulse' : 'text-gray-400 hover:text-primary'}`}
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">
                                                                {(isListening || isDictating) ? 'mic' : 'keyboard_voice'}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {ambiguityData && (
                                                <div className="mt-4">
                                                    <DraftItemRow 
                                                        item={{
                                                            name: ambiguityData.rawName,
                                                            amount: ambiguityData.amount,
                                                            unit: ambiguityData.unit,
                                                            brand: ambiguityData.brand,
                                                            price: getShoppingLinePrice(ambiguityData),
                                                            resolveStatus: ambiguityData.status || 'AMBIGUOUS',
                                                            candidates: ambiguityData.candidates
                                                        }}
                                                        index={0}
                                                        onResolve={(c) => {
                                                            openResolvedItemEditor({
                                                                id: c.id,
                                                                name: c.canonicalName || c.name,
                                                                amount: ambiguityData.amount || 1,
                                                                unit: ambiguityData.unit || c.canonicalUnit || 'adet',
                                                                brand: ambiguityData.brand || '',
                                                                price: getShoppingLinePrice(ambiguityData),
                                                                type: 'PRODUCT'
                                                            });
                                                            setAmbiguityData(null);
                                                        }}
                                                        onRemove={() => setAmbiguityData(null)}
                                                        onUpdate={(updates) => {
                                                            setAmbiguityData(prev => ({ ...prev, ...updates }));
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-8 flex flex-wrap gap-2 pt-2">
                                            {Object.values(cart).filter(i => {
                                                const itemType = i.category === 'Genel' ? 'GENERAL' : 'KITCHEN';
                                                return itemType === manualType && !inventory.find(inv => inv.name === i.name);
                                            }).map(tag => (
                                                <div key={tag.name} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-[10px] font-black text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                                    <span>{tag.name}</span>
                                                    <button onClick={() => toggleCheckbox(null, tag)} className="material-symbols-outlined text-[14px] hover:text-red-500">close</button>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-8">
                                            <button onClick={() => setIsManualModalOpen(false)} className="w-full h-14 bg-slate-900 dark:bg-slate-700 text-white rounded-2xl font-black text-xs shadow-xl shadow-slate-900/30 hover:bg-black transition-all flex items-center justify-center gap-3 uppercase tracking-wider">
                                                Alışverişe Devam Et
                                                <span className="material-symbols-outlined text-[18px]">check_circle</span>
                                            </button>
                                        </div>
                                    </motion.div>
                                </div>
                            )}
                        </AnimatePresence >

                        {/* NAME ENTRY MODAL (Mobile Flow) */}
                        <AnimatePresence>
                            {isNameModalOpen && (
                                <div className="fixed inset-0 z-[270] flex items-center justify-center bg-black/60 backdrop-blur-md p-6">
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                                        className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-[2.5rem] shadow-2xl p-8 relative border border-white/50 dark:border-gray-700"
                                    >
                                        <button onClick={() => setIsNameModalOpen(false)} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
                                            <span className="material-symbols-outlined text-2xl">close</span>
                                        </button>

                                        <div className="text-center mb-8">
                                            <div className="inline-flex p-4 bg-primary/10 rounded-full text-primary mb-4">
                                                <span className="material-symbols-outlined text-4xl">edit_document</span>
                                            </div>
                                            <h3 className="text-xl font-black text-gray-900 dark:text-white">Listeni İsimlendir</h3>
                                            <p className="text-xs text-gray-500 font-bold mt-2">Daha sonra kolayca bulmak için bir isim ver.</p>
                                        </div>

                                        <div className="space-y-6">
                                            <div>
                                                <input
                                                    ref={listNameRef}
                                                    autoFocus
                                                    value={listName}
                                                    onChange={(e) => {
                                                        setListName(e.target.value);
                                                        if (showNameError) setShowNameError(false);
                                                    }}
                                                    className={`w-full h-14 px-5 text-center text-sm font-bold border rounded-2xl ${showNameError ? 'border-red-500 ring-2 ring-red-500/50 animate-pulse' : 'border-gray-200 dark:border-gray-700'} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 outline-none transition-all placeholder-gray-400`}
                                                    placeholder={showNameError ? "Lütfen bir isim girin" : "Örn: Haftalık Pazar..."}
                                                />
                                            </div>

                                        </div>

                                        {/* Notification Selector (Mobile) */}
                                        <div className="py-2">
                                            <NotificationSelector
                                                onChange={setNotificationSettings}
                                                initialData={notificationSettings}
                                            />
                                        </div>

                                        <button
                                            onClick={() => handleSaveList(true)}
                                            disabled={isSubmitting}
                                            className="w-full h-14 bg-primary hover:bg-red-600 text-white rounded-2xl font-black text-xs shadow-xl shadow-primary/30 flex items-center justify-center gap-2 uppercase tracking-widest active:scale-[0.98] transition-all"
                                        >
                                            {isSubmitting ? 'Kaydediliyor...' : 'Kaydet ve Bitir'}
                                            <span className="material-symbols-outlined text-lg">check_circle</span>
                                        </button>
                                    </motion.div>
                                </div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </motion.div>
        )
        }
    </AnimatePresence >
    </>);
};

export default MarketListWizard;
